// src/lib/http/install.ts
// Wires the shared client once at boot: cookies on every same-origin call, the §6.10 401/403 policy, and the session keeper.

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { readErrorEnvelope } from "./authErrors";
import { http, isSameOriginUrl, requestPath } from "./client";
import { startSessionKeeper } from "./refresh";
import { subscribeAuthSession } from "./session";
import { decideAuthFailure, requestStepUp, requireEnrolment } from "./stepup";
import { purgeRetiredCookies } from "./retiredCookies";

type TrackedConfig = InternalAxiosRequestConfig & { __xoeSentAt?: number; __xoeRetried?: boolean; __xoeSteppedUp?: boolean };

// Credentials only on same-origin calls: a vendor answering `Access-Control-Allow-Origin: *` would fail a credentialed request.
export const attachSessionInterceptors = (instance: AxiosInstance): void => {
	instance.interceptors.request.use((config: TrackedConfig) => {
		if (isSameOriginUrl(config.url, config.baseURL)) {
			config.__xoeSentAt = Date.now();
			config.withCredentials = true;
			// Credentials travel only as HttpOnly cookies; a stray header is dropped rather than sent.
			config.headers?.delete?.("Authorization");
		}
		return config;
	});
	instance.interceptors.response.use(undefined, async (error: AxiosError) => {
		const config = error.config as TrackedConfig | undefined;
		const status = error.response?.status;
		if (!config || (status !== 401 && status !== 403) || !isSameOriginUrl(config.url, config.baseURL)) throw error;
		const { code, details } = await readErrorEnvelope(error.response?.data);
		const path = requestPath(config.url, config.baseURL);
		const action = await decideAuthFailure(status, code, details, path, config.__xoeSentAt ?? 0, Boolean(config.__xoeRetried));
		if (action === "stepup") {
			if (config.__xoeSteppedUp || !(await requestStepUp(details))) throw error;
			config.__xoeSteppedUp = true;
			return instance.request(config);
		}
		if (action === "enrol") void requireEnrolment(details);
		if (action !== "retry") throw error;
		config.__xoeRetried = true;
		return instance.request(config);
	});
};

let installed = false;

export const installHttpClient = ({ onSessionEnded }: { onSessionEnded?: () => void } = {}): void => {
	if (installed) return;
	installed = true;
	purgeRetiredCookies();
	attachSessionInterceptors(http);
	startSessionKeeper();
	if (onSessionEnded) {
		subscribeAuthSession((next, previous) => {
			if (next.status === "anonymous" && previous.status !== "anonymous") onSessionEnded();
		});
	}
};
